import { useState, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Row,
  Col,
  Card,
  Button,
  Input,
  Select,
  Tag,
  Typography,
  Space,
  Spin,
  Empty,
  Modal,
  Form,
  Tabs,
  Checkbox,
  message,
  Divider,
} from "antd";
import {
  SearchOutlined,
  ImportOutlined,
  PlusOutlined,
  BookOutlined,
  TagsOutlined,
  SendOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CloseCircleFilled,
  EditOutlined,
} from "@ant-design/icons";
import { api } from "@/services/api";
import { API_ENDPOINTS, DEBOUNCE_DELAY } from "@/constants";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type {
  LibraryQuestion,
  LibraryListResponse,
  LibraryQuestionStatus,
  Board,
  Deck,
  ApiResponse,
} from "@/types";

interface BoardWithSubjects extends Board {
  subjects: { id: string; name: string; colorHex: string | null }[];
}

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const SUBMISSION_STATUS_ICONS: Record<LibraryQuestionStatus, React.ReactNode> = {
  PENDING: <ClockCircleOutlined style={{ color: "#f59e0b" }} />,
  APPROVED: <CheckCircleOutlined style={{ color: "#22c55e" }} />,
  REJECTED: <CloseCircleOutlined style={{ color: "#ef4444" }} />,
};

const DIFFICULTY_COLORS = {
  EASY: "#22c55e",
  MEDIUM: "#f59e0b",
  HARD: "#ef4444",
};

export default function TeacherLibraryPage() {
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Browse state
  const [activeTab, setActiveTab] = useState("browse");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [boardFilter, setBoardFilter] = useState<string | undefined>();
  const [submitBoardId, setSubmitBoardId] = useState<string | undefined>();
  const [subjectFilter, setSubjectFilter] = useState<string | undefined>();
  const [difficultyFilter, setDifficultyFilter] = useState<string | undefined>();
  const [tagsFilter, setTagsFilter] = useState<string | undefined>();
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Import modal state
  const [importOpen, setImportOpen] = useState(false);
  const [importDeckId, setImportDeckId] = useState<string | undefined>();

  // Submit modal state
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitForm] = Form.useForm();

  // Edit submission modal state
  const [editOpen, setEditOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<LibraryQuestion | null>(null);
  const [editBoardId, setEditBoardId] = useState<string | undefined>();
  const [editForm] = Form.useForm();

  // ── Boards + subjects ─────────────────────────────────────────────────────
  const { data: boardsData } = useQuery<ApiResponse<BoardWithSubjects[]>>({
    queryKey: ["curriculum-boards"],
    queryFn: () => api.get(API_ENDPOINTS.CURRICULUM_BOARDS),
    staleTime: 5 * 60 * 1000,
  });
  const boards = boardsData?.data ?? [];
  const selectedBoard = boards.find((b) => b.id === boardFilter);
  const subjects = selectedBoard?.subjects ?? [];
  const allSubjects = boards.flatMap((b) => b.subjects);

  // ── Teacher's decks (for import target) ──────────────────────────────────
  const { data: decksData } = useQuery<ApiResponse<{ data: Deck[] }>>({
    queryKey: ["teacher-decks-for-library"],
    queryFn: () => api.get(API_ENDPOINTS.DECKS, { params: { status: "DRAFT", limit: 50 } }),
  });
  const draftDecks = decksData?.data?.data ?? [];

  // ── Library questions ─────────────────────────────────────────────────────
  const { data: listData, isLoading } = useQuery<ApiResponse<LibraryListResponse>>({
    queryKey: [
      "library-questions",
      subjectFilter,
      difficultyFilter,
      tagsFilter,
      debouncedSearch,
      cursor,
    ],
    queryFn: () =>
      api.get(API_ENDPOINTS.LIBRARY_QUESTIONS, {
        params: {
          ...(subjectFilter && { subjectId: subjectFilter }),
          ...(difficultyFilter && { difficulty: difficultyFilter }),
          ...(tagsFilter && { tags: tagsFilter }),
          ...(debouncedSearch && { search: debouncedSearch }),
          ...(cursor && { cursor }),
          limit: 20,
        },
      }),
    enabled: activeTab === "browse",
  });
  const questions = listData?.data?.items ?? [];
  const meta = listData?.data?.meta;

  // ── My submissions ────────────────────────────────────────────────────────
  const { data: submissionsData, isLoading: submissionsLoading } = useQuery<
    ApiResponse<LibraryQuestion[]>
  >({
    queryKey: ["library-my-submissions"],
    queryFn: () => api.get(API_ENDPOINTS.LIBRARY_MY_SUBMISSIONS),
    enabled: activeTab === "submissions",
  });
  const submissions = submissionsData?.data ?? [];

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSearchChange = useCallback((val: string) => {
    setSearch(val);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
      setCursor(undefined);
      setCursorStack([]);
    }, DEBOUNCE_DELAY);
  }, []);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleNextPage = () => {
    if (meta?.nextCursor) {
      setCursorStack((prev) => [...prev, cursor ?? ""]);
      setCursor(meta.nextCursor);
    }
  };

  const handlePrevPage = () => {
    const stack = [...cursorStack];
    const prev = stack.pop();
    setCursorStack(stack);
    setCursor(prev || undefined);
  };

  // ── Mutations ─────────────────────────────────────────────────────────────
  const importMutation = useMutation({
    mutationFn: ({ deckId, questionIds }: { deckId: string; questionIds: { id: string; source: string }[] }) =>
      api.post(API_ENDPOINTS.LIBRARY_IMPORT, { deckId, questions: questionIds }),
    onSuccess: (data) => {
      const { imported, skipped } = data?.data ?? {};
      message.success(
        `Imported ${imported} card${imported !== 1 ? "s" : ""}${skipped ? ` (${skipped} skipped — deck at capacity)` : ""}`
      );
      setImportOpen(false);
      setSelected(new Set());
      setImportDeckId(undefined);
      queryClient.invalidateQueries({ queryKey: ["teacher-decks-for-library"] });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error?.message ?? "Import failed";
      message.error(msg);
    },
  });

  const submitMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post(API_ENDPOINTS.LIBRARY_SUBMIT, data),
    onSuccess: () => {
      message.success("Submitted for review! An admin will approve it soon.");
      setSubmitOpen(false);
      submitForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ["library-my-submissions"] });
    },
    onError: () => message.error("Failed to submit question"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch(`${API_ENDPOINTS.LIBRARY_QUESTIONS}/${id}`, data),
    onSuccess: () => {
      message.success("Question updated and resubmitted for review.");
      setEditOpen(false);
      setEditingQuestion(null);
      editForm.resetFields();
      queryClient.invalidateQueries({ queryKey: ["library-my-submissions"] });
    },
    onError: () => message.error("Failed to update question"),
  });

  const openEdit = (q: LibraryQuestion) => {
    const boardForSubject = boards.find((b) =>
      b.subjects.some((s) => s.id === q.subject?.id)
    );
    setEditingQuestion(q);
    setEditBoardId(boardForSubject?.id);
    editForm.setFieldsValue({
      title: q.title ?? "",
      frontText: q.frontText,
      backText: q.backText ?? "",
      difficulty: q.difficulty,
      boardId: boardForSubject?.id,
      subjectId: q.subject?.id,
      tags: q.tags ?? [],
    });
    setEditOpen(true);
  };

  // ── Library question card ─────────────────────────────────────────────────
  const LibraryCard = ({ q }: { q: LibraryQuestion }) => {
    const isSelected = selected.has(q.id);
    return (
      <Card
        size="small"
        onClick={() => toggleSelect(q.id)}
        style={{
          marginBottom: 10,
          borderRadius: 10,
          cursor: "pointer",
          border: isSelected ? "2px solid #6366f1" : "1px solid #e2e8f0",
          background: isSelected ? "#f5f3ff" : "#fff",
          transition: "all 0.15s",
        }}
        styles={{ body: { padding: "10px 14px" } }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <Checkbox
            checked={isSelected}
            onChange={() => toggleSelect(q.id)}
            onClick={(e) => e.stopPropagation()}
            style={{ marginTop: 2, flexShrink: 0 }}
          />
          <div style={{ flex: 1 }}>
            {q.title && (
              <Text strong style={{ fontSize: 13, display: "block", marginBottom: 2 }}>
                {q.title}
              </Text>
            )}
            <Paragraph
              ellipsis={{ rows: 2 }}
              style={{ marginBottom: 4, fontSize: 13, color: "#334155" }}
            >
              {q.frontText}
            </Paragraph>
            {q.backText && (
              <Text style={{ fontSize: 11, color: "#64748b", display: "block", marginBottom: 4 }}>
                Answer: {q.backText.slice(0, 100)}{q.backText.length > 100 ? "…" : ""}
              </Text>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {q.subject && <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>{q.subject.name}</Tag>}
              {q.chapter && <Tag color="geekblue" style={{ fontSize: 10, margin: 0 }}>{q.chapter.name}</Tag>}
              <Tag
                style={{
                  margin: 0,
                  fontSize: 10,
                  color: DIFFICULTY_COLORS[q.difficulty],
                  border: `1px solid ${DIFFICULTY_COLORS[q.difficulty]}`,
                  background: "transparent",
                }}
              >
                {q.difficulty}
              </Tag>
              {q.tags.map((t) => (
                <Tag key={t} icon={<TagsOutlined />} style={{ fontSize: 10, margin: 0 }}>{t}</Tag>
              ))}
            </div>
          </div>
          <Text style={{ fontSize: 10, color: "#94a3b8", flexShrink: 0 }}>
            Used {q.usageCount}×
          </Text>
        </div>
      </Card>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Question Library</Title>
          <Text style={{ color: "#64748b", fontSize: 13 }}>
            Browse shared questions and import them into your decks
          </Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setSubmitOpen(true)}>
          Submit a Question
        </Button>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ marginBottom: 16 }}
        items={[
          { key: "browse", label: <span><BookOutlined /> Browse Library</span> },
          { key: "submissions", label: <span><SendOutlined /> My Submissions</span> },
        ]}
      />

      {/* ── Browse tab ── */}
      {activeTab === "browse" && (
        <>
          {selected.size === 0 && questions.length > 0 && !isLoading && (
            <Card size="small" style={{ background: '#efe6ff', border: '1px solid #c4b5fd', borderRadius: 8, marginBottom: 12 }} styles={{ body: { padding: '10px 14px' } }}>
              <Text style={{ color: '#5b21b6', fontSize: 13 }}>
                💡 <strong>Tip:</strong> Click on any question card or checkbox to select it. Once selected, you can import multiple questions into your deck at once.
              </Text>
            </Card>
          )}

          {/* Selection bar */}
          {selected.size > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#f5f3ff",
                border: "1px solid #6366f1",
                borderRadius: 8,
                padding: "8px 16px",
                marginBottom: 12,
              }}
            >
              <Text style={{ color: "#4338ca", fontWeight: 600 }}>
                {selected.size} question{selected.size !== 1 ? "s" : ""} selected
              </Text>
              <Space>
                <Button size="small" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
                <Button
                  type="primary"
                  size="small"
                  icon={<ImportOutlined />}
                  onClick={() => setImportOpen(true)}
                >
                  Import into Deck
                </Button>
              </Space>
            </div>
          )}

          {/* Filters */}
          <Row gutter={[10, 8]} style={{ marginBottom: 14 }}>
            <Col xs={24} md={8}>
              <Input
                placeholder="Search questions..."
                prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                suffix={
                  search ? (
                    <CloseCircleFilled
                      style={{ fontSize: 16, color: "#8c8c8c", cursor: "pointer" }}
                      onClick={() => handleSearchChange("")}
                    />
                  ) : null
                }
                style={{ width: isMobile ? "100%" : undefined }}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                allowClear
                placeholder="Board"
                style={{ width: "100%" }}
                value={boardFilter}
                onChange={(v) => { setBoardFilter(v); setSubjectFilter(undefined); setCursor(undefined); setCursorStack([]); }}
              >
                {boards.map((b) => (
                  <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                allowClear
                placeholder={boardFilter ? "Subject" : "Select board first"}
                disabled={!boardFilter}
                style={{ width: "100%" }}
                value={subjectFilter}
                onChange={(v) => { setSubjectFilter(v); setCursor(undefined); setCursorStack([]); }}
              >
                {subjects.map((s) => (
                  <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                allowClear
                placeholder="Difficulty"
                style={{ width: "100%" }}
                value={difficultyFilter}
                onChange={(v) => { setDifficultyFilter(v); setCursor(undefined); setCursorStack([]); }}
              >
                <Select.Option value="EASY">Easy</Select.Option>
                <Select.Option value="MEDIUM">Medium</Select.Option>
                <Select.Option value="HARD">Hard</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Input
                placeholder="Tag filter"
                prefix={<TagsOutlined />}
                value={tagsFilter}
                onChange={(e) => { setTagsFilter(e.target.value || undefined); setCursor(undefined); setCursorStack([]); }}
                suffix={
                  tagsFilter ? (
                    <CloseCircleFilled
                      style={{ fontSize: 16, color: "#8c8c8c", cursor: "pointer" }}
                      onClick={() => { setTagsFilter(undefined); setCursor(undefined); setCursorStack([]); }}
                    />
                  ) : null
                }
              />
            </Col>
          </Row>

          {isLoading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin />
            </div>
          ) : questions.length === 0 ? (
            <Empty description="No questions found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <>
              {questions.map((q) => (
                <LibraryCard key={q.id} q={q} />
              ))}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
                <Button size="small" disabled={cursorStack.length === 0} onClick={handlePrevPage}>
                  Previous
                </Button>
                <Button size="small" disabled={!meta?.hasMore} onClick={handleNextPage}>
                  Next
                </Button>
              </div>
            </>
          )}
        </>
      )}

      {/* ── My Submissions tab ── */}
      {activeTab === "submissions" && (
        <>
          {submissionsLoading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Spin />
            </div>
          ) : submissions.length === 0 ? (
            <Empty
              description="You haven't submitted any questions yet"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Button icon={<PlusOutlined />} onClick={() => setSubmitOpen(true)}>
                Submit Your First Question
              </Button>
            </Empty>
          ) : (
            submissions.map((q) => (
              <Card
                key={q.id}
                size="small"
                style={{ marginBottom: 10, borderRadius: 10, border: "1px solid #e2e8f0" }}
                styles={{ body: { padding: "10px 14px" } }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {q.title && <Text strong style={{ fontSize: 13 }}>{q.title}: </Text>}
                    <Text style={{ fontSize: 13 }}>{q.frontText}</Text>
                    {q.status === "REJECTED" && q.rejectionReason && (
                      <div
                        style={{
                          marginTop: 6,
                          padding: "4px 8px",
                          background: "#fef2f2",
                          borderRadius: 4,
                          fontSize: 11,
                          color: "#dc2626",
                        }}
                      >
                        Reason: {q.rejectionReason}
                      </div>
                    )}
                  </div>
                  <Space style={{ flexShrink: 0 }}>
                    {SUBMISSION_STATUS_ICONS[q.status]}
                    <Text style={{ fontSize: 12 }}>{q.status}</Text>
                    {(q.status === "PENDING" || q.status === "REJECTED") && (
                      <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEdit(q)}
                      >
                        Edit
                      </Button>
                    )}
                  </Space>
                </div>
              </Card>
            ))
          )}
        </>
      )}

      {/* Import deck picker modal */}
      <Modal
        title={<><ImportOutlined /> Import {selected.size} Question{selected.size !== 1 ? "s" : ""} into Deck</>}
        open={importOpen}
        onCancel={() => { setImportOpen(false); setImportDeckId(undefined); }}
        onOk={() => {
          if (!importDeckId) {
            message.warning("Please select a deck");
            return;
          }
          importMutation.mutate({ deckId: importDeckId, questionIds: Array.from(selected).map((id) => ({ id, source: "PLATFORM" })) });
        }}
        okText="Import"
        okButtonProps={{ icon: <ImportOutlined /> }}
        confirmLoading={importMutation.isPending}
        destroyOnHidden
      >
        <Divider style={{ margin: "12px 0" }} />
        <Text style={{ display: "block", marginBottom: 12, color: "#64748b" }}>
          Select a draft deck to import the selected questions into. They will be added as cards at the end of the deck.
        </Text>
        <Select
          style={{ width: "100%" }}
          placeholder="Select a draft deck"
          value={importDeckId}
          onChange={setImportDeckId}
          showSearch
          filterOption={(input, option) =>
            String(option?.children ?? "").toLowerCase().includes(input.toLowerCase())
          }
        >
          {draftDecks.map((d) => (
            <Select.Option key={d.id} value={d.id}>
              {d.topicName}{" "}
              <Text style={{ fontSize: 11, color: "#94a3b8" }}>
                ({d.cardCount} card{d.cardCount !== 1 ? "s" : ""})
              </Text>
            </Select.Option>
          ))}
        </Select>
        {draftDecks.length === 0 && (
          <Text style={{ fontSize: 12, color: "#94a3b8", display: "block", marginTop: 8 }}>
            No draft decks found. Create a deck first.
          </Text>
        )}
      </Modal>

      {/* Edit submission modal */}
      <Modal
        title={<><EditOutlined /> Edit Submission</>}
        open={editOpen}
        onCancel={() => { setEditOpen(false); setEditingQuestion(null); editForm.resetFields(); setEditBoardId(undefined); }}
        onOk={() =>
          editForm.validateFields().then((values) => {
            if (!editingQuestion) return;
            const { boardId: _boardId, ...payload } = values;
            updateMutation.mutate({
              id: editingQuestion.id,
              data: { ...payload, tags: payload.tags ?? [] },
            });
          })
        }
        okText="Save & Resubmit"
        okButtonProps={{ icon: <SendOutlined /> }}
        confirmLoading={updateMutation.isPending}
        width={600}
        destroyOnHidden
      >
        <Divider style={{ margin: "12px 0" }} />
        <Text style={{ display: "block", marginBottom: 16, color: "#64748b", fontSize: 13 }}>
          Update your question and it will be resubmitted for admin review.
        </Text>
        <Form form={editForm} layout="vertical" requiredMark={false}>
          <Form.Item name="title" label="Title (optional)" rules={[{ max: 50 }]}>
            <Input placeholder="Short title" maxLength={50} />
          </Form.Item>
          <Form.Item
            name="frontText"
            label="Question"
            rules={[{ required: true, message: "Question text is required" }, { max: 500 }]}
          >
            <TextArea rows={3} placeholder="Write your question here..." maxLength={500} showCount />
          </Form.Item>
          <Form.Item name="backText" label="Answer / Explanation (optional)" rules={[{ max: 500 }]}>
            <TextArea rows={2} placeholder="Provide the answer or explanation..." maxLength={500} showCount />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="boardId" label="Board">
                <Select
                  allowClear
                  placeholder="Select board"
                  onChange={(v) => {
                    setEditBoardId(v);
                    editForm.setFieldValue("subjectId", undefined);
                  }}
                >
                  {boards.map((b) => (
                    <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="difficulty" label="Difficulty" initialValue="MEDIUM">
                <Select>
                  <Select.Option value="EASY">Easy</Select.Option>
                  <Select.Option value="MEDIUM">Medium</Select.Option>
                  <Select.Option value="HARD">Hard</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="subjectId" label="Subject">
            <Select
              allowClear
              disabled={!editBoardId}
              placeholder={editBoardId ? "Select subject" : "Select board first"}
            >
              {(boards.find((b) => b.id === editBoardId)?.subjects ?? allSubjects).map((s) => (
                <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="tags" label="Tags (up to 10)">
            <Select mode="tags" placeholder="Type a tag and press Enter" maxCount={10} tokenSeparators={[","]} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Submit question modal */}
      <Modal
        title={<><PlusOutlined /> Submit a Question to the Library</>}
        open={submitOpen}
        onCancel={() => { setSubmitOpen(false); submitForm.resetFields(); setSubmitBoardId(undefined); }}
        onOk={() =>
          submitForm.validateFields().then((values) => {
            const { boardId: _boardId, ...payload } = values;
            submitMutation.mutate({ ...payload, tags: payload.tags ?? [] });
          })
        }
        okText="Submit for Review"
        okButtonProps={{ icon: <SendOutlined /> }}
        confirmLoading={submitMutation.isPending}
        width={600}
        destroyOnHidden
      >
        <Divider style={{ margin: "12px 0" }} />
        <Text style={{ display: "block", marginBottom: 16, color: "#64748b", fontSize: 13 }}>
          Your question will be reviewed by an admin before it appears in the shared library.
        </Text>
        <Form form={submitForm} layout="vertical" requiredMark={false}>
          <Form.Item name="title" label="Title (optional)" rules={[{ max: 50 }]}>
            <Input placeholder="Short title" maxLength={50} />
          </Form.Item>
          <Form.Item
            name="frontText"
            label="Question"
            rules={[{ required: true, message: "Question text is required" }, { max: 500 }]}
          >
            <TextArea rows={3} placeholder="Write your question here..." maxLength={500} showCount />
          </Form.Item>
          <Form.Item name="backText" label="Answer / Explanation (optional)" rules={[{ max: 500 }]}>
            <TextArea rows={2} placeholder="Provide the answer or explanation..." maxLength={500} showCount />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="boardId" label="Board">
                <Select
                  allowClear
                  placeholder="Select board"
                  onChange={(v) => {
                    setSubmitBoardId(v);
                    submitForm.setFieldValue("subjectId", undefined);
                  }}
                >
                  {boards.map((b) => (
                    <Select.Option key={b.id} value={b.id}>{b.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="difficulty" label="Difficulty" initialValue="MEDIUM">
                <Select>
                  <Select.Option value="EASY">Easy</Select.Option>
                  <Select.Option value="MEDIUM">Medium</Select.Option>
                  <Select.Option value="HARD">Hard</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="subjectId" label="Subject">
            <Select
              allowClear
              disabled={!submitBoardId}
              placeholder={submitBoardId ? "Select subject" : "Select board first"}
            >
              {(boards.find((b) => b.id === submitBoardId)?.subjects ?? allSubjects).map((s) => (
                <Select.Option key={s.id} value={s.id}>{s.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="tags" label="Tags (up to 10, helps teachers find your question)">
            <Select
              mode="tags"
              placeholder="Type a tag and press Enter"
              maxCount={10}
              tokenSeparators={[","]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
